import {
  CategoryMaster,
  ItemMaster,
  TypeMaster,
} from '../../../services/apiTypes';
import { MANAGE_ITEM_MODE } from '../../../_constants/manageConst';
import { API_CODE } from '../../../_constants/apiConst';
import { getTypeMaster, putTypeMaster } from '../../../services/api/typeMaster';
import {
  getCategoryMaster,
  putCategoryMaster,
} from '../../../services/api/categoryMaster';
import { getItemMaster, putItemMaster } from '../../../services/api/itemMaster';
import { useState } from 'react';
import {
  isGetCategoryMaster,
  isGetItemMaster,
  isGetTypeMaster,
} from '../../../_utils/typeGuardUtils';

type useSortChangeModeProps = {
  categoryMaster: CategoryMaster[];
  setCategoryMaster: React.Dispatch<React.SetStateAction<CategoryMaster[]>>;
  typeMaster: TypeMaster[];
  setTypeMaster: React.Dispatch<React.SetStateAction<TypeMaster[]>>;
  itemMaster: ItemMaster[];
  setItemMaster: React.Dispatch<React.SetStateAction<ItemMaster[]>>;
  setDisplayMode: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showApiError: (
    apiError: unknown,
    apiCode: string,
    onCloseCallback?: () => void,
  ) => void;
  showTypeError: (apiCode: string, onCloseCallback?: () => void) => void;
};

/** ManageInspectionItemで実行する並び替え関連のhook */
const useSortChangeMode = ({
  categoryMaster,
  setCategoryMaster,
  typeMaster,
  setTypeMaster,
  itemMaster,
  setItemMaster,
  setDisplayMode,
  setIsLoading,
  showApiError,
  showTypeError,
}: useSortChangeModeProps) => {
  /** 並び替え前の各項目 */
  const [beforeSortData, setBeforeSortData] = useState({
    categoryMaster,
    typeMaster,
    itemMaster,
  });

  /** 並び替えモードへ切替 */
  const onClickSort = () => {
    setBeforeSortData({ categoryMaster, typeMaster, itemMaster });
    // 全て見た目通りのindexになるようにitemMasterを更新する
    const newItemMaster: ItemMaster[] = [];
    categoryMaster.map((category) => {
      typeMaster
        .filter((t) => t.categoryId === category.categoryId)
        .map((type) => {
          itemMaster
            .filter((i0) => i0.depth === 0 && type.typeId === i0.typeId)
            .map((item0) => {
              newItemMaster.push(item0);
              itemMaster
                .filter(
                  (i1) =>
                    i1.depth === 1 &&
                    type.typeId === i1.typeId &&
                    i1.parentItemId === item0.itemId,
                )
                .map((item1) => {
                  newItemMaster.push(item1);
                  itemMaster
                    .filter(
                      (i2) =>
                        i2.depth === 2 &&
                        type.typeId === i2.typeId &&
                        i2.parentItemId === item1.itemId,
                    )
                    .map((item2) => {
                      newItemMaster.push(item2);
                    });
                });
            });
        });
    });
    setItemMaster(newItemMaster);
    setDisplayMode(MANAGE_ITEM_MODE.SORT);
  };

  /** 並び替えモードキャンセル終了 */
  const handleSortCancel = () => {
    // 並び替え前に戻す
    setCategoryMaster(beforeSortData.categoryMaster);
    setTypeMaster(beforeSortData.typeMaster);
    setItemMaster(beforeSortData.itemMaster);
    setDisplayMode(MANAGE_ITEM_MODE.VIEW);
  };

  /** 並び替えモード保存終了 */
  const onClickSortSave = async () => {
    setIsLoading(true);
    await Promise.all(
      categoryMaster.map(async (category, cIndex) => {
        // 移動済みの場合は新しいsortでPUTする
        if (category.sort !== cIndex) {
          try {
            await putCategoryMaster({ ...category, sort: cIndex });
          } catch (error) {
            showApiError(error, API_CODE.CATEGORY_MASTER);
            handleSortCancel();
            return;
          }
        }
        typeMaster
          .filter((t) => t.categoryId === category.categoryId)
          .map(async (type, tIndex) => {
            if (type.sort !== tIndex) {
              try {
                const { displayName, displayType, ...putType } = type;
                await putTypeMaster({ ...putType, sort: tIndex });
              } catch (error) {
                showApiError(error, API_CODE.TYPE_MASTER);
                handleSortCancel();
                return;
              }
            }
            itemMaster
              .filter((i) => i.typeId === type.typeId)
              .map(async (item, iIndex) => {
                if (item.sort !== iIndex) {
                  try {
                    await putItemMaster({ ...item, sort: iIndex });
                  } catch (error) {
                    showApiError(error, API_CODE.ITEM_MASTER);
                    handleSortCancel();
                    return;
                  }
                }
              });
          });
      }),
    ).finally(async () => {
      // 再取得する
      await getAllMaster();
      setDisplayMode(MANAGE_ITEM_MODE.VIEW);
      setIsLoading(false);
    });
  };

  /** マスタ取得 */
  const getAllMaster = async () => {
    try {
      const categoryRes = await getCategoryMaster();
      if (!isGetCategoryMaster(categoryRes)) {
        showTypeError(API_CODE.CATEGORY_MASTER);
      } else {
        setCategoryMaster(categoryRes);
      }
    } catch (error) {
      showApiError(error, API_CODE.CATEGORY_MASTER);
    }
    try {
      const typeRes = await getTypeMaster();
      if (!isGetTypeMaster(typeRes)) {
        showTypeError(API_CODE.TYPE_MASTER);
      } else {
        setTypeMaster(typeRes);
      }
    } catch (error) {
      showApiError(error, API_CODE.TYPE_MASTER);
    }
    try {
      const itemRes = await getItemMaster();
      if (!isGetItemMaster(itemRes)) {
        showTypeError(API_CODE.ITEM_MASTER);
      } else {
        setItemMaster(itemRes.itemList);
      }
    } catch (error) {
      showApiError(error, API_CODE.ITEM_MASTER);
    }
  };

  return {
    onClickSort,
    handleSortCancel,
    onClickSortSave,
  };
};

export default useSortChangeMode;
